/**
 * «Публикации» — две вкладки (задача Оли №376): «Черновики» (адрес без `tab`) и «Контент-план»
 * (`?tab=plan`); окна «Шаблоны» и «Аккаунты» — `?open=prompts|accounts`. Старые адреса
 * (`approved`, `accounts`, `prompts`) ведут туда, где эти вещи живут теперь. Типы ответа
 * `GET /api/posts/content-plan` (`reels/contentplan.py`).
 *
 * Контент-план — по одной сети за раз (№383, `?tab=plan&net=vk`), разделы «Отложенные» и «Опубликовано»,
 * на плитке контент поста и дата выкладки. Даты приходят БЕЗ часового пояса (как их ввели) — подписи
 * собираются из строки, без `Date`, чтобы время не уехало.
 * Спеки `docs/superpowers/specs/2026-10-22-posts-two-tabs-design.md`,
 * `docs/superpowers/specs/2026-10-22-posts-plan-redesign-design.md`.
 */

export type PostsTab = "drafts" | "plan";
export type PostsDialog = "prompts" | "accounts" | null;

const LEGACY: Record<string, { tab: PostsTab; open: PostsDialog }> = {
  approved: { tab: "plan", open: null },
  accounts: { tab: "plan", open: "accounts" },
  prompts: { tab: "drafts", open: "prompts" },
};

/** Вкладка и открытое окно по адресу; `open` старого адреса уступает явному. */
export function parseTabs(search: URLSearchParams): { tab: PostsTab; open: PostsDialog } {
  const raw = search.get("tab") ?? "";
  const legacy = LEGACY[raw];
  const tab: PostsTab = legacy ? legacy.tab : raw === "plan" ? "plan" : "drafts";
  const o = search.get("open");
  const open: PostsDialog = o === "prompts" || o === "accounts" ? o : (legacy?.open ?? null);
  return { tab, open };
}

/** Адрес вкладки и окна: «Черновики» без окна — пустой. */
export function tabsSearch(tab: PostsTab, open: PostsDialog = null): Record<string, string> {
  return { ...(tab === "plan" ? { tab: "plan" } : {}), ...(open ? { open } : {}) };
}

type Preview = {
  variant_id: number;
  asset_path: string;
  video_ms: number | null;
  poster_path: string | null;
  width: number | null;
  height: number | null;
  /** рамка этой сети на этом элементе (доли оригинала) */
  crop: { x: number; y: number; w: number; h: number } | null;
  title: string | null;
  text: string | null;
};

export type PlannedItem = Preview & {
  plan_id: number;
  series: string | null;
  media_count: number;
  accounts: string[];
  date_approve: string | null;
  /** дата выкладки (план), `null` — не назначена */
  publish_date: string | null;
  /** `date_modify` публикации — база ручки даты (`schedulePlan`, №396) */
  date_modify: string;
};

export type PublishedItem = Preview & {
  id: number;
  plan_id: number | null;
  url: string | null;
  date: string | null;
  author_name: string | null;
};

export type ContentPlanNetwork = {
  code: string;
  name: string;
  /** пропорция сети (`post_format`), у сетей без формата — `null` */
  aspect: string | null;
  planned: PlannedItem[];
  published: PublishedItem[];
};
export type ContentPlan = { networks: ContentPlanNetwork[] };

/** Превью плитки: у видео — постер (нет его — `null`), у картинки — сама картинка. */
export function previewPath(p: Preview): string | null {
  return p.video_ms !== null ? p.poster_path : p.asset_path;
}

/** Плиток в разделе до «Показать ещё» и сколько добавляет каждое нажатие. */
export const PLAN_PAGE = 8;

/** Сеть контент-плана: из адреса, иначе первая с отложенными, иначе первая; сетей нет — `null`. */
export function planNet(search: URLSearchParams, networks: Pick<ContentPlanNetwork, "code" | "planned">[]): string | null {
  const wanted = search.get("net");
  if (wanted && networks.some((n) => n.code === wanted)) return wanted;
  return (networks.find((n) => n.planned.length > 0) ?? networks[0])?.code ?? null;
}

const MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

/** «22 окт» из `2026-10-22…`. */
export function dayLabel(iso: string): string {
  const m = ISO.exec(iso);
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : iso;
}

/** «23 окт в 13:00» из `2026-10-23T13:00:00`. */
export function whenLabel(iso: string): string {
  const m = ISO.exec(iso);
  if (!m) return iso;
  return m[4] ? `${dayLabel(iso)} в ${m[4]}:${m[5]}` : dayLabel(iso);
}

/** Значение для `<input type="datetime-local">`. */
export function inputValue(iso: string | null): string {
  return iso ? iso.slice(0, 16) : "";
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Дата выкладки прошла, а пост всё ещё в «Отложенных». */
export function isOverdue(iso: string | null, now: Date = new Date()): boolean {
  if (!iso) return false;
  const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return inputValue(iso) < local;
}

/** «Отложенные» — с датой выкладки, «Не запланированы» — без неё (№390); порядок сервера внутри сохраняется. */
export function splitPlanned<T extends { publish_date: string | null }>(items: T[]): { scheduled: T[]; unscheduled: T[] } {
  return {
    scheduled: items.filter((x) => x.publish_date !== null),
    unscheduled: items.filter((x) => x.publish_date === null),
  };
}

/** Текст на плитке: заголовок и текст поста этой сети. */
export function snippet(p: { title: string | null; text: string | null }): string {
  return [p.title, p.text]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" — ");
}
